import { useEffect, useState, useCallback, useMemo } from "react";

/*
====================================================================
FACTORY FUNCTIONS
====================================================================
*/

const createLevelObject = (index = 1, outlineGeometry = null) => ({
  id: `level_${Date.now()}_${index}`,
  label: index === 1 ? "Ground Floor" : `Level ${index}`,
  elevation: index === 1 ? 0 : (index - 1) * 3,
  order: index,

  // géométrie structurelle du niveau
  outlineGeometry,

  // Areas dessinées dans le niveau
  entities: [],
});

const createOuvrageObject = (index = 1, footprintGeometry = null) => ({
  id: `ouvrage_${Date.now()}_${index}`,
  label: `Ouvrage ${index}`,

  // géométrie structurelle de l’ouvrage
  footprintGeometry,

  // niveaux de l’ouvrage
  levels: [],
});

const createSiteObject = (index = 1) => ({
  id: `site_${Date.now()}_${index}`,
  label: `Site ${index}`,

  // un site peut être vide au départ
  ouvrages: [],
});

/*
====================================================================
HOOK PRINCIPAL
====================================================================
*/

export default function useDrawSpaceData({
  projectId,
  assetId,
  enablePersistence = true,
}) {
  /*
  ================================================================
  STATES
  ================================================================
  */

  const [sites, setSites] = useState([]);

  const [activeSiteId, setActiveSiteId] = useState(null);
  const [activeOuvrageId, setActiveOuvrageId] = useState(null);
  const [activeLevelId, setActiveLevelId] = useState(null);

  const [loading, setLoading] = useState(false);

  /*
  ================================================================
  INITIALISATION
  ================================================================
  */

  useEffect(() => {
    if (!projectId) {
      setSites([]);
      setActiveSiteId(null);
      setActiveOuvrageId(null);
      setActiveLevelId(null);
      return;
    }

    setLoading(true);

    // Initialisation minimale saine :
    // un projet démarre avec un site, sans ouvrage ni niveau imposé.
    const initialSite = createSiteObject(1);

    setSites([initialSite]);
    setActiveSiteId(initialSite.id);
    setActiveOuvrageId(null);
    setActiveLevelId(null);

    setLoading(false);
  }, [projectId, assetId, enablePersistence]);

  /*
  ================================================================
  ÉLÉMENTS ACTIFS
  ================================================================
  */

  const activeSite = useMemo(() => {
    return sites.find((site) => site.id === activeSiteId) ?? null;
  }, [sites, activeSiteId]);

  const activeOuvrage = useMemo(() => {
    return (
      activeSite?.ouvrages?.find((ouvrage) => ouvrage.id === activeOuvrageId) ??
      null
    );
  }, [activeSite, activeOuvrageId]);

  const activeLevel = useMemo(() => {
    return (
      activeOuvrage?.levels?.find((level) => level.id === activeLevelId) ?? null
    );
  }, [activeOuvrage, activeLevelId]);

  /*
  ================================================================
  ENTITÉS DU NIVEAU ACTIF
  ================================================================
  */

  const getActiveEntities = useCallback(() => {
    return activeLevel?.entities ?? [];
  }, [activeLevel]);

  const updateEntitiesForActiveLevel = useCallback(
    (entities) => {
      if (!activeSiteId || !activeOuvrageId || !activeLevelId) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              return {
                ...ouvrage,
                levels: ouvrage.levels.map((level) => {
                  if (level.id !== activeLevelId) return level;

                  return {
                    ...level,
                    entities,
                  };
                }),
              };
            }),
          };
        })
      );
    },
    [activeSiteId, activeOuvrageId, activeLevelId]
  );

  /*
  ================================================================
  UPDATE STRUCTURE
  ================================================================
  */

  const updateOuvrageById = useCallback(
    (ouvrageId, patch) => {
      if (!activeSiteId || !ouvrageId || !patch) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) =>
              ouvrage.id === ouvrageId ? { ...ouvrage, ...patch } : ouvrage
            ),
          };
        })
      );
    },
    [activeSiteId]
  );

  const updateLevelById = useCallback(
    (levelId, patch) => {
      if (!activeSiteId || !activeOuvrageId || !levelId || !patch) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              return {
                ...ouvrage,
                levels: ouvrage.levels.map((level) =>
                  level.id === levelId ? { ...level, ...patch } : level
                ),
              };
            }),
          };
        })
      );
    },
    [activeSiteId, activeOuvrageId]
  );

  /*
  ================================================================
  GESTION DES SITES
  ================================================================
  */

  const createSite = useCallback(() => {
    const nextIndex = sites.length + 1;
    const newSite = createSiteObject(nextIndex);

    setSites((prev) => [...prev, newSite]);

    setActiveSiteId(newSite.id);
    setActiveOuvrageId(null);
    setActiveLevelId(null);
  }, [sites.length]);

  const deleteSite = useCallback(
    (siteId) => {
      if (!siteId) return;

      const nextSites = sites.filter((site) => site.id !== siteId);
      setSites(nextSites);

      if (nextSites.length > 0) {
        const nextSite = nextSites[0];
        const nextOuvrage = nextSite.ouvrages[0] ?? null;
        const nextLevel = nextOuvrage?.levels?.[0] ?? null;

        setActiveSiteId(nextSite.id);
        setActiveOuvrageId(nextOuvrage?.id ?? null);
        setActiveLevelId(nextLevel?.id ?? null);
      } else {
        setActiveSiteId(null);
        setActiveOuvrageId(null);
        setActiveLevelId(null);
      }
    },
    [sites]
  );

  /*
  ================================================================
  GESTION DES OUVRAGES
  ================================================================
  */

  const createOuvrage = useCallback(() => {
    if (!activeSiteId) return;

    const newOuvrage = createOuvrageObject(
      (activeSite?.ouvrages?.length ?? 0) + 1,
      null
    );

    setSites((prev) =>
      prev.map((site) => {
        if (site.id !== activeSiteId) return site;

        return {
          ...site,
          ouvrages: [...site.ouvrages, newOuvrage],
        };
      })
    );

    setActiveOuvrageId(newOuvrage.id);
    setActiveLevelId(null);
  }, [activeSiteId, activeSite]);

  const createOuvrageFromGeometry = useCallback(
    (geometry) => {
      if (!activeSiteId || !geometry) return;

      const newOuvrage = createOuvrageObject(
        (activeSite?.ouvrages?.length ?? 0) + 1,
        geometry
      );

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: [...site.ouvrages, newOuvrage],
          };
        })
      );

      setActiveOuvrageId(newOuvrage.id);
      setActiveLevelId(null);
    },
    [activeSiteId, activeSite]
  );

  const deleteOuvrage = useCallback(
    (ouvrageId) => {
      if (!activeSiteId || !ouvrageId) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          const nextOuvrages = site.ouvrages.filter(
            (ouvrage) => ouvrage.id !== ouvrageId
          );

          const nextOuvrage = nextOuvrages[0] ?? null;
          const nextLevel = nextOuvrage?.levels?.[0] ?? null;

          setActiveOuvrageId(nextOuvrage?.id ?? null);
          setActiveLevelId(nextLevel?.id ?? null);

          return {
            ...site,
            ouvrages: nextOuvrages,
          };
        })
      );
    },
    [activeSiteId]
  );

  /*
  ================================================================
  GESTION DES NIVEAUX
  ================================================================
  */

  const createLevel = useCallback(() => {
    if (!activeSiteId || !activeOuvrageId) return;

    setSites((prev) =>
      prev.map((site) => {
        if (site.id !== activeSiteId) return site;

        return {
          ...site,
          ouvrages: site.ouvrages.map((ouvrage) => {
            if (ouvrage.id !== activeOuvrageId) return ouvrage;

            const newLevel = createLevelObject(
              ouvrage.levels.length + 1,
              null
            );

            setActiveLevelId(newLevel.id);

            return {
              ...ouvrage,
              levels: [...ouvrage.levels, newLevel],
            };
          }),
        };
      })
    );
  }, [activeSiteId, activeOuvrageId]);

  const createLevelFromGeometry = useCallback(
    (geometry) => {
      if (!activeSiteId || !activeOuvrageId || !geometry) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              const newLevel = createLevelObject(
                ouvrage.levels.length + 1,
                geometry
              );

              setActiveLevelId(newLevel.id);

              return {
                ...ouvrage,
                levels: [...ouvrage.levels, newLevel],
              };
            }),
          };
        })
      );
    },
    [activeSiteId, activeOuvrageId]
  );

  const deleteLevel = useCallback(
    (levelId) => {
      if (!activeSiteId || !activeOuvrageId || !levelId) return;

      setSites((prev) =>
        prev.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              const nextLevels = ouvrage.levels.filter(
                (level) => level.id !== levelId
              );

              const nextLevel = nextLevels[0] ?? null;
              setActiveLevelId(nextLevel?.id ?? null);

              return {
                ...ouvrage,
                levels: nextLevels,
              };
            }),
          };
        })
      );
    },
    [activeSiteId, activeOuvrageId]
  );

  /*
  ================================================================
  EXPORT
  ================================================================
  */

  return {
    sites,
    setSites,

    activeSiteId,
    setActiveSiteId,

    activeOuvrageId,
    setActiveOuvrageId,

    activeLevelId,
    setActiveLevelId,

    activeSite,
    activeOuvrage,
    activeLevel,

    getActiveEntities,
    updateEntitiesForActiveLevel,

    updateOuvrageById,
    updateLevelById,

    createSite,
    deleteSite,

    createOuvrage,
    createOuvrageFromGeometry,
    deleteOuvrage,

    createLevel,
    createLevelFromGeometry,
    deleteLevel,

    loading,
  };
}